import { Component, EventEmitter, Output } from '@angular/core';
import { ReviewsClient } from '../../client/reviews.client';
import { Session } from '../entity/entities';

/**
 * G2 in-app review prompt (#28067) — our own control layer over the G2 embed.
 *
 * G2's first embed step is a single "Leave Feedback" button with no dismiss /
 * opt-out, so the proactive prompt (snooze, don't-show-again, funnel analytics)
 * has to be ours. Reuses <gs-dialog> for consistent chrome; content + footer are
 * projected so we fully control copy and the actions.
 *
 * Wiring:
 *  - open()            -> called by the PDP-4 trigger (30s inactivity), gated on
 *                         Session.g2Review?.Promptable + cadence (PDP-7).
 *  - leaveReview()     -> opens the G2 embed (PDP-5); PDP-6 emits 'opened'.
 *  - notNow()          -> snooze (PDP-7); PDP-6 emits 'dismissed'.
 *                         If the "don't show again" checkbox is ticked, this is an
 *                         explicit opt-out instead (BE-6/PDP-6 postG2Event('opt_out')).
 *
 * Opt-out is a checkbox + "Not now", NOT its own button: a stray click on a
 * button shouldn't permanently silence the prompt — opt-out must be deliberate.
 */
@Component({
  selector: 'g2-review-popup',
  templateUrl: './g2ReviewPopup.component.html',
  styleUrls: ['./g2ReviewPopup.component.scss'],
})
export class G2ReviewPopupComponent {
  visible = false;
  dontShowAgain = false;

  // G2 embed (PDP-5)
  embedVisible = false;
  embedUrl: string | null = null;
  embedLoading = false;
  embedError: string | null = null; // real failure (red)
  embedInfo: string | null = null; // benign note, e.g. already reviewed (neutral)

  @Output() onLeaveReview = new EventEmitter<void>();
  @Output() onNotNow = new EventEmitter<void>(); // snooze (dismissed)
  @Output() onDontShowAgain = new EventEmitter<void>(); // opt-out
  @Output() onEmbedClosed = new EventEmitter<void>(); // PDP-7 refresh-on-close hook

  // Session-scoped link cache (Sloj 1 prefetch). The G2 state is valid 24h, so once
  // fetched we reuse it for this session — "Leave a review" opens instantly instead of
  // waiting on review_status + mint. This component is a root singleton, so the fields
  // live for the whole SPA session; `_cachedForUser` invalidates the cache on a
  // logout/login user switch (no page reload).
  private _prefetchedUrl: string | null = null;
  private _prefetchedCode: string | null = null; // BE-8 error code if the fetch failed
  private _prefetchedMessage: string | null = null; // client-safe message
  private _linkPromise: Promise<void> | null = null;
  private _cachedForUser: number | null = null;

  // ALREADY_REVIEWED / NOT_ELIGIBLE aren't failures — they mean "no prompt needed".
  // The login `promptable` flag comes from OUR cache (never calls G2), so it can be
  // stale; getG2Link is the live reconciliation point. On these codes we suppress the
  // prompt / show a friendly note instead of a red error.
  private static readonly BENIGN_CODES = ['ALREADY_REVIEWED', 'NOT_ELIGIBLE'];

  constructor(private reviewsClient: ReviewsClient) {}

  /** Fire-and-forget funnel event (PDP-6 → BE-6). Never blocks or surfaces errors. */
  private emitEvent(eventType: string): void {
    this.reviewsClient.postG2Event(eventType).catch(() => {});
  }

  /** Show the prompt. Callers own the eligibility/cadence gate. */
  open(): void {
    this.dontShowAgain = false; // never carry a stale opt-out into a fresh prompt
    this.visible = true;
    this.emitEvent('prompt_shown');
    // Warm the G2 link in the background so "Leave a review" is instant. Fire-and-forget:
    // we do NOT auto-close the popup on the verdict (that caused a show-then-hide flash) —
    // the popup shows per the login cache; an already-reviewed edge is handled on click.
    this.ensureLink();
  }

  close(): void {
    this.visible = false;
  }

  leaveReview(): void {
    // Engaging with the review overrides the checkbox — they won't be prompted
    // again once a review exists anyway.
    this.close();
    this.markNotPromptable(); // don't re-pop the proactive prompt this session
    this.onLeaveReview.emit();
    this.openEmbed();
  }

  notNow(): void {
    const optOut = this.dontShowAgain;
    this.close();
    // Any deliberate action stops the proactive popup for THIS session (prevents the
    // 30s idle trigger from re-popping right after). Cross-session cadence
    // ([14d,30d]→stop) + persisted opt-out come in PDP-7.
    this.markNotPromptable();
    this.emitEvent(optOut ? 'opt_out' : 'dismissed'); // BE-6 also flips OptedOut on opt_out
    if (optOut) this.onDontShowAgain.emit();
    else this.onNotNow.emit();
  }

  /**
   * Shows the embed dialog, using the prefetched link if it's ready (instant),
   * otherwise waiting on the in-flight/first fetch with a spinner.
   * Public so a manual "Leave a review" menu entry can open it directly (no popup).
   */
  openEmbed(): void {
    this.emitEvent('opened');
    this.embedVisible = true;
    this.embedUrl = null;
    this.embedError = null;
    this.embedInfo = null;

    // Ready (from prefetch or already resolved) → apply immediately.
    if (this._prefetchedUrl || this._prefetchedCode) {
      this.applyEmbedVerdict();
      return;
    }

    // Not ready yet — show a spinner and resolve when the fetch completes.
    this.embedLoading = true;
    this.ensureLink().then(() => this.applyEmbedVerdict());
  }

  /** Renders the embed dialog from the cached fetch result: url / benign note / error. */
  private applyEmbedVerdict(): void {
    this.embedLoading = false;
    if (this._prefetchedUrl) {
      this.embedUrl = this._prefetchedUrl;
    } else if (this.isBenign(this._prefetchedCode)) {
      this.embedInfo = this.benignMessage(this._prefetchedCode);
      this.markNotPromptable();
    } else {
      this.embedError = this._prefetchedMessage || this.genericEmbedError;
    }
  }

  /**
   * Fetches the G2 embed URL once and caches it for the session (state is 24h).
   * Fire-and-forget from open(); awaited from openEmbed() if the click beats the fetch.
   * Errors are captured (BE-8 code + client-safe Message) and surfaced on use.
   */
  private ensureLink(): Promise<void> {
    // Invalidate a link cached for a different user (SPA logout/login keeps this singleton alive).
    const currentUser = Session?.userCode ?? null;
    if (this._cachedForUser !== currentUser) {
      this._prefetchedUrl = null;
      this._prefetchedCode = null;
      this._prefetchedMessage = null;
      this._linkPromise = null;
      this._cachedForUser = currentUser;
    }

    if (this._prefetchedUrl || this._prefetchedCode) return Promise.resolve();
    if (this._linkPromise) return this._linkPromise;

    this._linkPromise = this.reviewsClient
      .getG2Link()
      .then((res) => {
        const url = res?.result?.Url;
        if (url) this._prefetchedUrl = url;
        else {
          this._prefetchedCode = 'UNKNOWN';
          this._prefetchedMessage = this.genericEmbedError;
        }
      })
      .catch((err) => {
        // BE-8 envelope: { Error: { Code, Message } } — Message is client-safe.
        this._prefetchedCode = err?.error?.Error?.Code || 'UNKNOWN';
        this._prefetchedMessage = err?.error?.Error?.Message || this.genericEmbedError;
      })
      .finally(() => {
        this._linkPromise = null;
      });
    return this._linkPromise;
  }

  closeEmbed(): void {
    this.embedVisible = false;
    this.embedUrl = null;
    this.embedInfo = null;
    this.onEmbedClosed.emit();
  }

  private isBenign(code: string | null): boolean {
    return !!code && G2ReviewPopupComponent.BENIGN_CODES.indexOf(code) > -1;
  }

  private benignMessage(code: string | null): string {
    return code === 'ALREADY_REVIEWED'
      ? "Looks like you've already left a review — thank you!"
      : 'This review isn’t available for your account.';
  }

  /** A stale promptable flag was corrected by the live check — don't re-prompt this session. */
  private markNotPromptable(): void {
    if (Session?.g2Review) Session.g2Review.Promptable = false;
  }

  private get genericEmbedError(): string {
    return 'We couldn’t open the review form right now. Please try again later.';
  }
}
